import * as React from "react";
import PropTypes from "prop-types";
import { useFranchises, useDebounce } from "../../../hooks";
import { Button, Chip, TextInput } from "../../../themeV2";
import css from "./AdvancedSettings.module.css";
import { updateInfluencer } from "./service";

export default function FranchiseAssociation({
  influencerId,
  currentFranchiseId,
  currentFranchiseName,
  triggerAlert,
}) {
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  useDebounce(() => setDebouncedQuery(searchQuery), 300, [searchQuery]);

  const { franchises } = useFranchises(
    debouncedQuery.length >= 2 ? debouncedQuery : null,
  );

  const handleAssign = React.useCallback(
    async (franchiseId) => {
      setSaving(true);
      try {
        await updateInfluencer(influencerId, { franchise_id: franchiseId });
        triggerAlert({ message: "Franchise updated" });
        setSearchOpen(false);
        setSearchQuery("");
        setDebouncedQuery("");
      } catch (err) {
        triggerAlert({ message: err.message, severity: "error" });
      }
      setSaving(false);
    },
    [influencerId, triggerAlert],
  );

  const handleRemove = React.useCallback(async () => {
    setSaving(true);
    try {
      await updateInfluencer(influencerId, { franchise_id: null });
      triggerAlert({ message: "Franchise removed" });
    } catch (err) {
      triggerAlert({ message: err.message, severity: "error" });
    }
    setSaving(false);
  }, [influencerId, triggerAlert]);

  return (
    <div>
      {currentFranchiseId ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
          }}
        >
          <span
            style={{
              font: "var(--type-body-lg)",
              letterSpacing: "var(--tracking-body-lg)",
            }}
          >
            {currentFranchiseName ?? `Franchise #${currentFranchiseId}`}
          </span>
          <Chip label="Remove" onDelete={handleRemove} disabled={saving} />
        </div>
      ) : (
        <p className={css.caption}>No franchise assigned</p>
      )}

      <div style={{ marginBlockStart: "var(--space-2)" }}>
        <Button
          variant="outlined"
          onClick={() => setSearchOpen((prev) => !prev)}
        >
          {searchOpen
            ? "Cancel"
            : currentFranchiseId
            ? "Change Franchise"
            : "Assign Franchise"}
        </Button>
      </div>

      {searchOpen && (
        <div className={css.searchPanel}>
          <TextInput
            label="Search by franchise name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            description="Type to search franchises"
            autoFocus
          />

          {saving && <p className={css.caption}>Saving...</p>}

          {franchises.length > 0 && (
            <ul className={css.resultList}>
              {franchises.map((franchise) => (
                <li
                  key={franchise.franchise_id}
                  className={css.resultItem}
                  role="button"
                  tabIndex={0}
                  aria-disabled={saving}
                  onClick={() => handleAssign(franchise.franchise_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleAssign(franchise.franchise_id);
                    }
                  }}
                >
                  {franchise.name}
                </li>
              ))}
            </ul>
          )}

          {debouncedQuery.length >= 2 && !saving && franchises.length === 0 && (
            <p className={css.caption}>
              No franchises found. Create one in the Franchise Management page.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

FranchiseAssociation.propTypes = {
  influencerId: PropTypes.number.isRequired,
  currentFranchiseId: PropTypes.number,
  currentFranchiseName: PropTypes.string,
  triggerAlert: PropTypes.func.isRequired,
};
